import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as EditIcon } from "../../../../../Assets/ActionIcons/EditIcon.svg";
import { PaymentRoutes } from "../../../../../Contracts/Routes";

export interface IntegrationCardProps {
  integrationTitle: string;
  integrationDescription: string;
  logoSvg: string;
  connected: boolean;
  editRoute: PaymentRoutes;
  disconnectConfirmationTitle: ReactNode;
  disconnectConfirmationDescription: ReactNode;
  onConnect: () => void;
  onDisconnect: () => Promise<void>;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integrationTitle,
  integrationDescription,
  logoSvg,
  connected,
  onConnect,
  onDisconnect,
  editRoute,
  disconnectConfirmationTitle,
  disconnectConfirmationDescription
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDisconnectModalVisible, setIsDisconnectModalVisible] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const disconnectModalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        disconnectModalRef.current &&
        !disconnectModalRef.current.contains(event.target as Node)
      ) {
        setIsDisconnectModalVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const disconnectClicked = () => {
    setIsDisconnectModalVisible(true);
  };

  const onDisconnectConfirmed = useCallback(async () => {
    setDisconnectLoading(true);
    onDisconnect && (await onDisconnect());
    setDisconnectLoading(false);
    setIsDisconnectModalVisible(false);
  }, [onDisconnect]);

  return (
    <div className="flex flex-col border border-gray-200 relative w-72 h-64 rounded">
      <div className="flex p-6 w-full self-center border-b border-gray-200 justify-center">
        <img src={logoSvg} alt={integrationTitle} />
      </div>
      {connected ? (
        <div className="p-4 flex-grow flex items-center justify-center">
          <a
            className="text-blue-600 flex justify-between items-center px-4 py-2 border border-blue-500 rounded hover:bg-blue-200 hover:shadow-lg hover:scale-105"
            href={editRoute}
          >
            <EditIcon className="mr-2" />
            Edit
          </a>
        </div>
      ) : (
        <div className="p-4 flex-grow">{integrationDescription}</div>
      )}
      <button
        className={`p-2 min-h-10 font-semibold text-white rounded-b ${
          connected ? "bg-green-600 hover:bg-red-500" : "bg-blue-600 hover:bg-blue-400"
        }`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={connected ? disconnectClicked : onConnect}
      >
        <span>{connected ? (isHovered ? "Disconnect" : "Connected") : "Connect"}</span>
      </button>
      {isDisconnectModalVisible && (
        <div className="z-40">
          <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
            <div
              className="bg-white flex flex-col rounded shadow-lg max-w-[48rem]"
              ref={disconnectModalRef}
            >
              <div className="p-4 flex flex-col space-y-4 mt-2">
                {disconnectConfirmationTitle}
                {disconnectConfirmationDescription}
              </div>
              <div className="p-4 space-x-4 ml-auto">
                <button
                  className={`text-white px-4 py-1 rounded ${
                    disconnectLoading
                      ? "cursor-progress bg-gray-300"
                      : "bg-gray-600 hover:bg-gray-400"
                  }`}
                  onClick={() => setIsDisconnectModalVisible(false)}
                  disabled={disconnectLoading}
                >
                  No
                </button>
                <button
                  className={`text-white px-4 py-1 rounded ${
                    disconnectLoading
                      ? "cursor-progress bg-gray-300 hover:bg-gray-300"
                      : "bg-red-600 hover:bg-red-400"
                  }`}
                  onClick={onDisconnectConfirmed}
                  disabled={disconnectLoading}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
          <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
        </div>
      )}
    </div>
  );
};

export default IntegrationCard;
